import { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useMgmtOptions, datePreset } from "../hooks/useMgmtOptions";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/layout/PageHeader";
import { formatINR, formatNum, exportCSV } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Download, Database } from "lucide-react";

const EMPTY = {
  date: new Date().toISOString().slice(0, 10), employee_id: "", branch_id: "", campaign: "", vertical: "",
  service: "", platform: "", ad_spend: "", leads: "", qualified_leads: "", consultations: "", sales: "",
  revenue: "", clicks: "", impressions: "", whatsapp_leads: "", retargeting_leads: "", remark: "",
};

export default function DailyFunnel() {
  const opts = useMgmtOptions();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState(datePreset("this_month"));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/mgmt/funnel", { params: { start: range.start, end: range.end } });
    setRows(data);
  }, [range.start, range.end]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => {
    setForm({ ...EMPTY, ...r });
    setEditId(r.id); setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      ["ad_spend", "leads", "qualified_leads", "consultations", "sales", "revenue", "clicks", "impressions", "whatsapp_leads", "retargeting_leads"].forEach((k) => { payload[k] = Number(payload[k] || 0); });
      if (editId) await api.put(`/mgmt/funnel/${editId}`, payload);
      else await api.post("/mgmt/funnel", payload);
      toast.success(editId ? "Entry updated" : "Entry added");
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/mgmt/funnel/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const inp = "bg-[#0F172A] border-white/10 text-white h-10 focus-visible:ring-[#38BDF8]";
  const numFields = [
    ["ad_spend", "Ad Spend ₹"], ["leads", "Leads"], ["qualified_leads", "Qualified Leads"],
    ["consultations", "Consultations"], ["sales", "Sales / Treatments"], ["revenue", "Revenue ₹"],
    ["clicks", "Clicks (opt)"], ["impressions", "Impressions (opt)"], ["whatsapp_leads", "WhatsApp Leads (opt)"],
    ["retargeting_leads", "Retargeting Leads (opt)"],
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader title="Daily Funnel" subtitle="The single source of truth — enter raw numbers, the system computes everything"
        action={
          <div className="flex gap-2">
            <Button data-testid="funnel-export" onClick={() => exportCSV(rows, "daily_funnel.csv")} className="h-9 bg-[#0F172A] hover:bg-[#334155] border border-white/10 text-slate-300 rounded-full text-xs"><Download className="w-3.5 h-3.5 mr-1.5" />CSV</Button>
            <Button data-testid="add-funnel-btn" onClick={openNew} className="h-9 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full text-xs"><Plus className="w-4 h-4 mr-1.5" />Add Entry</Button>
          </div>
        } />

      <div className="flex items-center gap-2">
        <Database className="w-4 h-4 text-[#38BDF8]" />
        <span className="text-sm text-slate-400">Period</span>
        <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 w-40 bg-[#1E293B] border-white/10 text-white text-xs" />
        <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 w-40 bg-[#1E293B] border-white/10 text-white text-xs" />
        <span className="text-xs text-slate-500">{rows.length} entries</span>
      </div>

      <div className="rounded-xl bg-[#1E293B] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]">
          <thead><tr className="text-slate-400 text-xs uppercase tracking-wider border-b border-white/10">
            <th className="text-left py-2.5 px-3">Date</th><th className="text-left py-2.5 px-3">Employee</th><th className="text-left py-2.5 px-3">Campaign</th>
            <th className="text-left py-2.5 px-3">Vertical</th><th className="text-right py-2.5 px-3">Spend</th><th className="text-right py-2.5 px-3">Leads</th>
            <th className="text-right py-2.5 px-3">Qual</th><th className="text-right py-2.5 px-3">Consult</th><th className="text-right py-2.5 px-3">Sales</th>
            <th className="text-right py-2.5 px-3">Revenue</th><th className="text-right py-2.5 px-3">CPL</th><th className="text-right py-2.5 px-3">ROAS</th><th className="py-2.5 px-3"></th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className={`border-b border-white/5 hover:bg-[#334155]/40 cursor-pointer ${i % 2 ? "bg-[#0F172A]/40" : ""}`} onClick={() => openEdit(r)}>
                <td className="py-2.5 px-3 text-slate-300">{r.date}</td>
                <td className="py-2.5 px-3 text-slate-200">{r.employee_name}</td>
                <td className="py-2.5 px-3 text-slate-300">{r.campaign}</td>
                <td className="py-2.5 px-3 text-slate-400 text-xs">{r.vertical}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatINR(r.ad_spend)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatNum(r.leads)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatNum(r.qualified_leads)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatNum(r.consultations)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatNum(r.sales)}</td>
                <td className="py-2.5 px-3 text-right tabular text-[#10B981]">{formatINR(r.revenue)}</td>
                <td className="py-2.5 px-3 text-right tabular">{formatINR(r.cpl)}</td>
                <td className="py-2.5 px-3 text-right tabular text-[#38BDF8]">{r.roas}x</td>
                <td className="py-2.5 px-3 text-right">{isAdmin && <button data-testid={`del-funnel-${r.id}`} onClick={(e) => { e.stopPropagation(); del(r.id); }} className="text-[#EF4444] hover:opacity-70"><Trash2 className="w-4 h-4" /></button>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={13} className="py-8 text-center text-slate-500">No data available for the selected filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "Add"} Funnel Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Date</Label>
              <Input data-testid="funnel-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inp} /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="funnel-employee" className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-60">{opts.employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="funnel-branch" className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-60">{opts.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Vertical</Label>
              <Select value={form.vertical} onValueChange={(v) => setForm({ ...form, vertical: v })}>
                <SelectTrigger data-testid="funnel-vertical" className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">{opts.verticals.map((v) => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Campaign</Label>
              <Select value={form.campaign} onValueChange={(v) => setForm({ ...form, campaign: v })}>
                <SelectTrigger data-testid="funnel-campaign" className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-60">{opts.campaigns.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Service</Label>
              <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                <SelectTrigger className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200 max-h-60">{opts.services.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-400">Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger className={inp}><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#1E293B] border-white/10 text-slate-200">{opts.platforms.map((p) => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            {numFields.map(([k, label]) => (
              <div key={k} className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label>
                <Input data-testid={`funnel-${k}`} type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={inp} /></div>
            ))}
            <div className="space-y-1.5 col-span-2 md:col-span-3"><Label className="text-xs text-slate-400">Remark</Label>
              <Input value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} className={inp} /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-funnel-btn" onClick={save} disabled={busy} className="w-full h-11 bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0F172A] font-semibold rounded-full">{busy ? "Saving…" : "Save Entry"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
